/**
 * Review screen for AI-assisted Legacy Book draft — edit sections before export.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { aiLegacyBookApi } from '../../utils/aiLegacyBook';

export function AiLegacyBookReviewModal({
  open,
  onOpenChange,
  treeId,
  draft,
  onDraftUpdated,
  onMarkReviewed,
}) {
  const sections = draft?.sections || {};
  const [openingNote, setOpeningNote] = useState('');
  const [stories, setStories] = useState([]);
  const [personSummaries, setPersonSummaries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!open || !sections) return;
    setOpeningNote(sections.opening_note || '');
    setStories(sections.story_chapters || []);
    setPersonSummaries(sections.person_summaries || []);
  }, [open, sections]);

  if (!draft?.sections) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif">Review before export</DialogTitle>
            <DialogDescription>
              Your book draft isn&apos;t ready yet — use Prepare with AI to gather what you&apos;ve already preserved.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const handleSaveEdits = async () => {
    if (!treeId) return;
    setSaving(true);
    try {
      const updatedSections = {
        ...sections,
        opening_note: openingNote,
        story_chapters: stories,
        person_summaries: personSummaries,
      };
      const data = await aiLegacyBookApi.updateSections(treeId, updatedSections);
      onDraftUpdated?.(data.draft);
      toast.success('Draft saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleAccept = async () => {
    if (!treeId) return;
    setAccepting(true);
    try {
      const updatedSections = {
        ...sections,
        opening_note: openingNote,
        story_chapters: stories,
        person_summaries: personSummaries,
      };
      const data = await aiLegacyBookApi.updateSections(treeId, updatedSections);
      onDraftUpdated?.(data.draft);
      await onMarkReviewed?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not accept draft.');
    } finally {
      setAccepting(false);
    }
  };

  const missing = sections.missing_sections || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-[hsl(38_16%_86%)] bg-[hsl(42_28%_99%)]">
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-xl">Review your book draft</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            Edit any section below. AI only organises and lightly polishes your preserved material — it does not invent
            facts. Accept when you are ready to export.
            {draft.ai_polished ? (
              <span className="mt-1 block text-[11px] text-[#8b7355]">Some sections were AI-polished for readability.</span>
            ) : (
              <span className="mt-1 block text-[11px] text-muted-foreground">
                Assembled from your archive without AI polish (AI unavailable or not configured).
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Opening note</Label>
            <Textarea
              value={openingNote}
              onChange={(e) => setOpeningNote(e.target.value)}
              rows={3}
              className="font-serif text-sm leading-relaxed"
            />
          </div>

          {personSummaries.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Person summaries</p>
              {personSummaries.map((p, idx) => (
                <div key={p.id || idx} className="space-y-1.5 rounded-lg border border-border/40 bg-muted/10 p-3">
                  <p className="text-sm font-medium">{p.title}</p>
                  <Textarea
                    value={p.body || ''}
                    onChange={(e) => {
                      const next = [...personSummaries];
                      next[idx] = { ...p, body: e.target.value };
                      setPersonSummaries(next);
                    }}
                    rows={3}
                    className="text-sm"
                  />
                  {p.source === 'placeholder' ? (
                    <p className="text-[10px] text-muted-foreground italic">Placeholder — add a profile or story in your archive.</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          {stories.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Story chapters</p>
              {stories.map((s, idx) => (
                <div key={s.id || idx} className="space-y-1.5 rounded-lg border border-border/40 bg-white/80 p-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-medium">{s.title}</p>
                    {s.attribution ? (
                      <p className="text-[10px] text-muted-foreground">— {s.attribution}</p>
                    ) : null}
                  </div>
                  {s.ai_generated ? (
                    <p className="text-[10px] text-[#8b7355]">AI-polished section</p>
                  ) : null}
                  <Textarea
                    value={s.body || ''}
                    onChange={(e) => {
                      const next = [...stories];
                      next[idx] = { ...s, body: e.target.value };
                      setStories(next);
                    }}
                    rows={4}
                    className="font-serif text-sm leading-relaxed"
                  />
                </div>
              ))}
            </div>
          )}

          {missing.length > 0 && (
            <div className="rounded-lg border border-dashed border-amber-200/80 bg-amber-50/50 px-4 py-3">
              <p className="text-xs font-medium text-amber-900/90">Suggested additions (review only)</p>
              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-amber-900/80">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-2 border-t border-border/40 pt-4">
          <Button
            type="button"
            className="rounded-full gap-1.5"
            disabled={accepting || saving}
            onClick={handleAccept}
            data-testid="ai-book-accept-btn"
          >
            {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Accept draft for export
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            disabled={saving || accepting}
            onClick={handleSaveEdits}
          >
            {saving ? 'Saving…' : 'Save edits'}
          </Button>
          <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AiLegacyBookReviewModal;
